import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import { useInvertedLocalize } from '../../hooks';

import loadTurnstileScript from './loadTurnstileScript';

export interface TurnstileWidgetProps {
  siteKey?: string;
  theme?: 'auto' | 'light' | 'dark';
  size?: 'normal' | 'compact' | 'flexible';
  appearance?: 'always' | 'execute' | 'interaction-only';
  isDisabled?: boolean;
  onToken?: (token: string) => void;
  onExpired?: () => void;
}

export interface TurnstileWidgetRef {
  reset: () => void;
}

const DEFAULT_TEST_SITE_KEY = '1x00000000000000000000AA';

const TurnstileWidget = forwardRef<TurnstileWidgetRef, TurnstileWidgetProps>(
  function TurnstileWidget(
    {
      siteKey = DEFAULT_TEST_SITE_KEY,
      theme = 'light',
      size = 'flexible',
      appearance = 'always',
      isDisabled = false,
      onToken,
      onExpired,
    },
    ref
  ) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<string | null>(null);

    const onTokenRef = useRef(onToken);
    const onExpiredRef = useRef(onExpired);

    useEffect(() => {
      onTokenRef.current = onToken;
    }, [onToken]);

    useEffect(() => {
      onExpiredRef.current = onExpired;
    }, [onExpired]);

    useImperativeHandle(ref, () => ({
      reset: () => {
        const wid = widgetIdRef.current;

        if (wid && window.turnstile) {
          window.turnstile.reset(wid);
        }
      },
    }));

    const l = useInvertedLocalize();

    useEffect(() => {
      if (isDisabled) {
        return;
      }

      let isActive = true;

      loadTurnstileScript()
        .then(() => {
          if (!isActive) {
            return;
          }

          const el = containerRef.current;

          if (!el || !window.turnstile) {
            return;
          }

          const prev = widgetIdRef.current;

          if (prev) {
            window.turnstile.remove(prev);
            widgetIdRef.current = null;
          }

          el.innerHTML = '';

          const id = window.turnstile.render(el, {
            sitekey: siteKey,
            theme,
            size,
            appearance,
            callback: (token) => onTokenRef.current?.(token),
            'expired-callback': () => {
              onExpiredRef.current?.();
              const wid = widgetIdRef.current;

              if (wid && window.turnstile) {
                window.turnstile.reset(wid);
              }
            },
          });

          widgetIdRef.current = id ?? null;
        })
        .catch(() => {
          // silent for builder
        });

      // eslint-disable-next-line consistent-return
      return () => {
        isActive = false;
        const id = widgetIdRef.current;

        if (id && window.turnstile) {
          window.turnstile.remove(id);
          widgetIdRef.current = null;
        }
      };
    }, [siteKey, theme, size, appearance, isDisabled]);

    return <div ref={containerRef} aria-label={l('Cloudflare Turnstile')} />;
  }
);

export default TurnstileWidget;
